import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import colors from '../../../styled/colors';
import { TriangleFillIcon, CirclesLineIcon } from '../iconsSVG/icons';
import mediaQueries from '../../../utils/mediaQueries';

const Section = styled.section`
  background: ${colors.white};
  padding: 78px 24px 54px;
  @media ${mediaQueries.mdUp} {
    padding: 70px 83px 63px;
  }
`;
const TitleContainer = styled.div``;
const Title = styled.h2`
  color: ${colors.black3E};
  font-size: 18px;
  position: relative;
  text-align: center;
  z-index: 1;
  @media ${mediaQueries.mdUp} {
    font-size: 28px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 32px;
  }
  svg {
    position: absolute;
    z-index: -1;
  }
  .triangle-fill {
    left: calc(50% - 113px);
    top: -16px;
    @media ${mediaQueries.mdUp} {
      width: 61px;
      height: 57px;
      left: calc(50% - 28px);
      top: -30px;
    }
    @media ${mediaQueries.lgPlusUp} {
      left: calc(50% - 214px);
      top: -25px;
    }
  }
  .circles-line {
    bottom: 18px;
  }
`;
const HighlightText = styled.span`
  font-size: 20px;
  font-weight: bold;
  @media ${mediaQueries.mdUp} {
    font-size: 31px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 36px;
  }
`;
const Content = styled.div`
  margin-top: 28px;
  @media ${mediaQueries.mdUp} {
    display: flex;
    align-items: center;
    margin-top: 63px;
  }
  @media ${mediaQueries.lgPlusUp} {
    justify-content: center;
  }
`;
const VideoContainer = styled.div`
  display: flex;
  justify-content: center;
`;
const Video = styled.video`
  width: 184px;
  height: 340px;
`;
const Steps = styled.div`
  margin-top: 22px;
  @media ${mediaQueries.mdUp} {
    margin-top: 0;
    margin-left: 93px;
  }
`;
const Step = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  &:not(:last-child) {
    margin-bottom: 14px;
    @media ${mediaQueries.mdUp} {
      margin-bottom: 42px;
    }
    @media ${mediaQueries.xlPlusUp} {
      margin-bottom: 36px;
    }
  }
`;
const StepCircle = styled.div`
  align-items: center;
  background: ${colors.secondary};
  border-radius: 50%;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
  @media ${mediaQueries.mdUp} {
    width: 36px;
    height: 36px;
  }
  @media ${mediaQueries.xlPlusUp} {
    width: 40px;
    height: 40px;
  }
`;
const StepNumber = styled.span`
  color: ${colors.black3E};
  font-size: 20px;
  font-weight: bold;
  @media ${mediaQueries.mdUp} {
    font-size: 22px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 30px;
  }
`;
const StepText = styled.p`
  color: ${colors.black3E};
  font-size: 14px;
  flex: 1;
  line-height: 16px;
  margin-top: 0;
  margin-bottom: 0;
  margin-left: 8px;
  @media ${mediaQueries.mdUp} {
    font-size: 16px;
    line-height: 26px;
    margin-left: 21px;
  }
  @media ${mediaQueries.xlPlusUp} {
    font-size: 18px;
    margin-left: 12px;
  }
`;

const Simulation = () => {
  const intl = useIntl();
  const [flowVideoUrl, setFlowVideoUrl] = useState(
    'https://prod-production-socialpiper-cdn.s3.amazonaws.com/static/landing-instagram/videos/flow-publication-en.mp4',
  );
  const [flowVideoWebmUrl, setFlowVideoWebmUrl] = useState(
    'https://prod-production-socialpiper-cdn.s3.amazonaws.com/static/landing-instagram/videos/flow-publication-en.webm',
  );

  useEffect(() => {
    if (intl.locale === 'es') {
      setFlowVideoUrl(
        'https://prod-production-socialpiper-cdn.s3.amazonaws.com/static/landing-instagram/videos/flow-publication.mp4',
      );
      setFlowVideoWebmUrl(
        'https://prod-production-socialpiper-cdn.s3.amazonaws.com/static/landing-instagram/videos/flow-publication.webm',
      );
    }
  }, [intl]);
  return (
    <Section>
      <TitleContainer>
        <Title>
          <FormattedMessage
            id="landing.instagram.simulation.title"
            defaultMessage="<h>It\'s easy;</h> check it out:"
            values={{
              h: (...chunks) => <HighlightText>{chunks}</HighlightText>,
            }}
          />
          <TriangleFillIcon className="triangle-fill" />
          <CirclesLineIcon className="circles-line" />
        </Title>
      </TitleContainer>
      <Content>
        <VideoContainer>
          <Video autoPlay loop muted playsInline>
            <source
              src={flowVideoWebmUrl}
              type="video/webm"
            />
            <source
              src={flowVideoUrl}
              type="video/mp4"
            />
            Your browser does not support HTML5 video.
          </Video>
        </VideoContainer>
        <Steps>
          <Step>
            <StepCircle>
              <StepNumber>1</StepNumber>
            </StepCircle>
            <StepText>
              <FormattedMessage
                id="landing.instagram.simulation.steps.step1.text"
                defaultMessage="Choose your content and add your text."
              />
            </StepText>
          </Step>
          <Step>
            <StepCircle>
              <StepNumber>2</StepNumber>
            </StepCircle>
            <StepText>
              <FormattedMessage
                id="landing.instagram.simulation.steps.step2.text"
                defaultMessage="Select on which platforms to post."
              />
            </StepText>
          </Step>
          <Step>
            <StepCircle>
              <StepNumber>3</StepNumber>
            </StepCircle>
            <StepText>
              <FormattedMessage
                id="landing.instagram.simulation.steps.step3.text"
                defaultMessage="See recommendations for the best time or choose your own."
              />
            </StepText>
          </Step>
          <Step>
            <StepCircle>
              <StepNumber>4</StepNumber>
            </StepCircle>
            <StepText>
              <FormattedMessage
                id="landing.instagram.simulation.steps.step4.text"
                defaultMessage="Ready! Your content is live."
              />
            </StepText>
          </Step>
        </Steps>
      </Content>
    </Section>
  );
};

export default Simulation;
